import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import type { SpaceProps } from 'styled-system';
import { space } from 'styled-system';

import type { ButtonKind } from 'common/components/Button';
import { ButtonLink } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import { useIsObserved } from 'common/hooks/intersection-observer';
import type { AsProps, TextDecorationProps } from 'common/theme/system-utilities';
import { textDecoration } from 'common/theme/system-utilities';
import { Element } from 'common/types/tracking';
import { dispatchCtaButtonsClickEvent, dispatchCtaButtonsViewEvent } from './tracking';

interface CtaElementBase {
  link?: string;
}
interface CtaButton extends CtaElementBase {
  label: string;
  kind: ButtonKind;
}

interface CtaImage extends CtaElementBase {
  image: {
    url: string;
    alt: string;
  };
}

type CtaElement = CtaButton | CtaImage;

interface CtaButtonsProps {
  items: CtaElement[];
}

const UnstyledWrapper = styled(Flex)``;

const Wrapper = (props: ComponentProps<typeof UnstyledWrapper>) => (
  <UnstyledWrapper justifyContent="center" alignItems="center" flexWrap="wrap" {...props} />
);

const MARGIN_X = ['sp_8', 'sp_12'];
const MARGIN_Y = ['sp_4', 'sp_8', null, 'sp_12'];

const ImageContainer = styled.div<SpaceProps & TextDecorationProps & AsProps>`
  ${space};
  ${textDecoration};
`;

const imageContainerStyles: ComponentProps<typeof ImageContainer> = {
  mx: MARGIN_X,
  my: MARGIN_Y,
  textDecoration: 'none',
};

const UnstyledStyledButtonLink = styled(ButtonLink)<any>`
  ${space};
`;

const StyledButtonLink = (props: ComponentProps<typeof UnstyledStyledButtonLink>) => (
  <UnstyledStyledButtonLink mx={MARGIN_X} my={MARGIN_Y} {...props} />
);

const CtaButtons = ({ items }: CtaButtonsProps) => {
  const [ref, isObserved] = useIsObserved();

  useEffect(() => {
    // after the component is observed, dispatch the view event for each cta element
    if (isObserved) {
      items.map(ctaElement => {
        if ('image' in ctaElement) {
          dispatchCtaButtonsViewEvent(ctaElement.image.alt, Element.Image);
        } else {
          dispatchCtaButtonsViewEvent(ctaElement.label, Element.Button);
        }
      });
    }
  }, [isObserved]);

  return (
    <Wrapper data-corgi-id="cta-buttons" ref={ref}>
      {items.map((ctaElement, index) =>
        'image' in ctaElement ? (
          <ImageContainer
            key={index}
            {...imageContainerStyles}
            {...(ctaElement.link && {
              href: ctaElement.link,
              onClick: () => dispatchCtaButtonsClickEvent(ctaElement.image.alt, Element.Image),
              as: 'a',
            })}
          >
            <img loading="lazy" src={ctaElement.image.url} alt={ctaElement.image.alt} />
          </ImageContainer>
        ) : (
          <StyledButtonLink
            key={index}
            kind={ctaElement.kind}
            href={ctaElement.link}
            onClick={() => dispatchCtaButtonsClickEvent(ctaElement.label, Element.Button)}
          >
            {ctaElement.label}
          </StyledButtonLink>
        )
      )}
    </Wrapper>
  );
};

export default CtaButtons;
